import { useEffect, useMemo } from 'react';
import {
  UseFormGetFieldState,
  UseFormResetField,
  UseFormSetValue,
} from 'react-hook-form';
import {
  useGetInvoiceReservationPartiesLookupQuery,
  useGetInvoiceReservationWithAssociationsByIdQuery,
} from '../Service/reservationsLookupApi';
import type { CreateInvoiceForm } from '../Types/createInvoiceFormTypes';
import { normalizeInvoiceScheme } from '../utils/invoiceEnumNormalization';

export type PrefillFields = {
  buyerName?: string;
  buyerStreetNo?: string;
  buyerAddress?: string;
  buyerNip?: string;
  buyerCountryIso?: string;
  useDifferentRecipient?: boolean;
  recipientName?: string;
  recipientStreetNo?: string;
  recipientAddress?: string;
  recipientNip?: string;
  saleDate?: string;
};

type PartyPrefillData = {
  name?: string;
  streetNo?: string;
  address?: string;
  nip?: string;
  countryIso?: string;
};

const DEFAULT_BUYER_COUNTRY_ISO = 'PL';

const normalizeBuyerCountryIso = (countryIso?: string | null): string => {
  const normalized = countryIso?.trim().toUpperCase();
  return normalized || DEFAULT_BUYER_COUNTRY_ISO;
};

const splitAddressSummary = (addressSummary?: string | null) => {
  const [streetNo = '', ...rest] =
    addressSummary
      ?.split(',')
      .map((segment) => segment.trim())
      .filter(Boolean) ?? [];

  return {
    streetNo,
    address: rest.join(', '),
  };
};

const isPartySnapshotIncomplete = (party: PartyPrefillData): boolean =>
  !party.name || !party.streetNo || !party.address || !party.nip;

const mergePartyDataWithLookup = (
  snapshot: PartyPrefillData,
  lookupParty?: {
    displayName?: string;
    taxId?: string;
    addressSummary?: string;
  }
): PartyPrefillData => {
  const splitAddress = splitAddressSummary(lookupParty?.addressSummary);

  return {
    name: snapshot.name || lookupParty?.displayName || '',
    streetNo: snapshot.streetNo || splitAddress.streetNo || '',
    address: snapshot.address || splitAddress.address || '',
    nip: snapshot.nip || lookupParty?.taxId || '',
    countryIso: snapshot.countryIso,
  };
};

export const useReservationPrefill = (
  reservationId?: number,
  setValue?: UseFormSetValue<CreateInvoiceForm>,
  getFieldState?: UseFormGetFieldState<CreateInvoiceForm>,
  resetField?: UseFormResetField<CreateInvoiceForm>,
  useReservationPaymentImport?: boolean
) => {
  const { data: reservationDetails } = useGetInvoiceReservationWithAssociationsByIdQuery(
    reservationId!,
    {
      skip: !reservationId,
    }
  );

  const invoiceOrApplicantPartyId =
    reservationDetails?.invoiceBuyerPartyId ?? reservationDetails?.applicantPartyId;
  const hasFundingParty = Boolean(reservationDetails?.fundingPartyId);
  const buyerPartyId = hasFundingParty
    ? reservationDetails?.fundingPartyId
    : invoiceOrApplicantPartyId;
  const recipientPartyId = hasFundingParty ? invoiceOrApplicantPartyId : undefined;

  const buyerSnapshot: PartyPrefillData = useMemo(
    () =>
      hasFundingParty
        ? {}
        : {
            name: reservationDetails?.invoiceBuyerName,
            streetNo: reservationDetails?.invoiceBuyerStreet,
            address: reservationDetails?.invoiceBuyerAddress,
            nip: reservationDetails?.invoiceBuyerNip,
            countryIso: reservationDetails?.invoiceBuyerCountryIso,
          },
    [
      hasFundingParty,
      reservationDetails?.invoiceBuyerAddress,
      reservationDetails?.invoiceBuyerCountryIso,
      reservationDetails?.invoiceBuyerName,
      reservationDetails?.invoiceBuyerNip,
      reservationDetails?.invoiceBuyerStreet,
    ]
  );
  const recipientSnapshot: PartyPrefillData = useMemo(
    () =>
      hasFundingParty
        ? {
            name: reservationDetails?.invoiceBuyerName,
            streetNo: reservationDetails?.invoiceBuyerStreet,
            address: reservationDetails?.invoiceBuyerAddress,
            nip: reservationDetails?.invoiceBuyerNip,
          }
        : {},
    [
      hasFundingParty,
      reservationDetails?.invoiceBuyerAddress,
      reservationDetails?.invoiceBuyerName,
      reservationDetails?.invoiceBuyerNip,
      reservationDetails?.invoiceBuyerStreet,
    ]
  );

  const shouldLookupBuyerParty = Boolean(buyerPartyId) && isPartySnapshotIncomplete(buyerSnapshot);
  const shouldLookupRecipientParty =
    Boolean(recipientPartyId) && isPartySnapshotIncomplete(recipientSnapshot);
  const lookupPartyIds = useMemo(
    () =>
      Array.from(
        new Set(
          [
            shouldLookupBuyerParty ? buyerPartyId : undefined,
            shouldLookupRecipientParty ? recipientPartyId : undefined,
          ].filter(
            (value): value is number =>
              typeof value === 'number' && Number.isFinite(value) && value > 0
          )
        )
      ),
    [buyerPartyId, recipientPartyId, shouldLookupBuyerParty, shouldLookupRecipientParty]
  );
  const shouldLookupParties = lookupPartyIds.length > 0;

  const {
    data: partiesLookup,
    isFetching: isPartiesLookupFetching,
    isLoading: isPartiesLookupLoading,
    isUninitialized: isPartiesLookupUninitialized,
  } = useGetInvoiceReservationPartiesLookupQuery(
    {
      ids: shouldLookupParties ? lookupPartyIds : undefined,
      take: shouldLookupParties ? lookupPartyIds.length : undefined,
    },
    {
      skip: !shouldLookupParties,
    }
  );

  useEffect(() => {
    if (!reservationDetails || !setValue) return;
    if (
      typeof reservationId === 'number' &&
      Number.isFinite(reservationId) &&
      reservationDetails.id !== reservationId
    ) {
      return;
    }

    const isPartiesLookupPending =
      shouldLookupParties &&
      (isPartiesLookupFetching || isPartiesLookupLoading || isPartiesLookupUninitialized);
    const isPartiesLookupResolved = !shouldLookupParties || !isPartiesLookupPending;
    const partiesById = new Map(
      (isPartiesLookupResolved ? partiesLookup ?? [] : []).map((party) => [party.id, party])
    );
    const resolvedBuyerData = mergePartyDataWithLookup(
      buyerSnapshot,
      buyerPartyId ? partiesById.get(buyerPartyId) : undefined
    );
    const resolvedRecipientData = mergePartyDataWithLookup(
      recipientSnapshot,
      recipientPartyId ? partiesById.get(recipientPartyId) : undefined
    );
    const buyerName = resolvedBuyerData.name || '';
    const buyerStreetNo = resolvedBuyerData.streetNo || '';
    const buyerAddress = resolvedBuyerData.address || '';
    const buyerNip = resolvedBuyerData.nip || '';
    const buyerCountryIso = normalizeBuyerCountryIso(resolvedBuyerData.countryIso);
    const shouldUseDifferentRecipient = Boolean(recipientPartyId);
    const recipientName = resolvedRecipientData.name || '';
    const recipientStreetNo = resolvedRecipientData.streetNo || '';
    const recipientAddress = resolvedRecipientData.address || '';
    const recipientNip = resolvedRecipientData.nip || '';

    const toYMD = (s?: string) => (s && s.length >= 10 ? s.slice(0, 10) : undefined);
    const saleDateFromBooking =
      toYMD(reservationDetails.booking?.bookingDateTo) ||
      toYMD((reservationDetails.booking as any)?.dateTo) ||
      toYMD((reservationDetails as any).bookingDateTo) ||
      toYMD(reservationDetails.dateTo);
    const resolvedInvoiceScheme =
      reservationDetails.resolvedInvoiceScheme ?? reservationDetails.invoiceSchemeSnapshot;
    const summary = reservationDetails.paymentSummary;
    const importablePaidAmount = summary?.importablePaidAmount ?? 0;
    const isReservationPaymentImportAvailable = importablePaidAmount > 0;
    const suggestedPaymentMethod = summary?.suggestedPaymentMethod;
    const suggestedPaidAt = toYMD(summary?.suggestedPaidAt) || undefined;
    const suggestedPaymentComment =
      summary?.suggestedPaymentComment ||
      (summary?.isMixedPayment ? 'Zaimportowano wpłaty mieszane z rezerwacji.' : undefined);

    type ReservationPrefillField =
      | 'buyerName'
      | 'buyerStreetNo'
      | 'buyerAddress'
      | 'buyerNip'
      | 'buyerCountryIso'
      | 'useDifferentRecipient'
      | 'recipientName'
      | 'recipientStreetNo'
      | 'recipientAddress'
      | 'recipientNip'
      | 'saleDate'
      | 'scheme'
      | 'paidAmount'
      | 'paidAt'
      | 'paymentMethod'
      | 'paymentComment'
      | 'useReservationPaymentImport';

    const canAutofillField = (fieldName: ReservationPrefillField) => {
      const fieldState = getFieldState?.(fieldName);
      return !fieldState || (!fieldState.isDirty && !fieldState.isTouched);
    };

    const setAutofillField = (
      fieldName:
        | 'buyerName'
        | 'buyerStreetNo'
        | 'buyerAddress'
        | 'buyerNip'
        | 'buyerCountryIso'
        | 'recipientName'
        | 'recipientStreetNo'
        | 'recipientAddress'
        | 'recipientNip',
      value: string
    ) => {
      if (!canAutofillField(fieldName)) {
        return;
      }

      resetField?.(fieldName, { defaultValue: value });
      setValue?.(fieldName, value, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    };

    // Autofill RHF fields only while they are still pristine.
    if (isPartiesLookupResolved) {
      setAutofillField('buyerName', buyerName);
      setAutofillField('buyerStreetNo', buyerStreetNo);
      setAutofillField('buyerAddress', buyerAddress);
      setAutofillField('buyerNip', buyerNip);
      setAutofillField('buyerCountryIso', buyerCountryIso);

      if (canAutofillField('useDifferentRecipient')) {
        setValue?.('useDifferentRecipient', shouldUseDifferentRecipient, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      }

      if (shouldUseDifferentRecipient) {
        setAutofillField('recipientName', recipientName);
        setAutofillField('recipientStreetNo', recipientStreetNo);
        setAutofillField('recipientAddress', recipientAddress);
        setAutofillField('recipientNip', recipientNip);
      } else {
        setAutofillField('recipientName', '');
        setAutofillField('recipientStreetNo', '');
        setAutofillField('recipientAddress', '');
        setAutofillField('recipientNip', '');
      }
    }
    // Business rule: service date in invoice follows reservation end date.
    if (saleDateFromBooking && canAutofillField('saleDate')) {
      resetField?.('saleDate', { defaultValue: saleDateFromBooking });
      setValue?.('saleDate', saleDateFromBooking, {
        shouldValidate: true,
        shouldDirty: false,
        shouldTouch: false,
      });
    }

    if (
      resolvedInvoiceScheme !== undefined &&
      resolvedInvoiceScheme !== null &&
      canAutofillField('scheme')
    ) {
      const normalizedScheme = normalizeInvoiceScheme(resolvedInvoiceScheme);
      resetField?.('scheme', { defaultValue: normalizedScheme });
      setValue?.('scheme', normalizedScheme, {
        shouldValidate: true,
        shouldDirty: false,
        shouldTouch: false,
      });
    }

    if (useReservationPaymentImport !== false && isReservationPaymentImportAvailable) {
      if (canAutofillField('useReservationPaymentImport')) {
        setValue?.('useReservationPaymentImport', true, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      }

      if (canAutofillField('paidAmount')) {
        resetField?.('paidAmount', { defaultValue: importablePaidAmount });
        setValue?.('paidAmount', importablePaidAmount, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      }

      if (suggestedPaidAt && canAutofillField('paidAt')) {
        resetField?.('paidAt', { defaultValue: suggestedPaidAt });
        setValue?.('paidAt', suggestedPaidAt, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      }

      if (suggestedPaymentMethod && canAutofillField('paymentMethod')) {
        resetField?.('paymentMethod', { defaultValue: suggestedPaymentMethod });
        setValue?.('paymentMethod', suggestedPaymentMethod, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      }

      if (suggestedPaymentComment && canAutofillField('paymentComment')) {
        resetField?.('paymentComment', { defaultValue: suggestedPaymentComment });
        setValue?.('paymentComment', suggestedPaymentComment, {
          shouldValidate: false,
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    }
  }, [
    buyerSnapshot,
    getFieldState,
    isPartiesLookupFetching,
    isPartiesLookupLoading,
    isPartiesLookupUninitialized,
    partiesLookup,
    recipientPartyId,
    recipientSnapshot,
    reservationDetails,
    resetField,
    setValue,
    shouldLookupParties,
    useReservationPaymentImport,
    buyerPartyId,
    reservationId,
  ]);

  return { reservationDetails };
};
